/**
 * Franchise-partner fetch for InspectorLayout - resolves the FP record
 * (name, country, connection flags, resources) for the route's fp_code,
 * with abort-on-unmount, request deduplication and 404/403 -> not-found
 * handling.
 *
 * Extracted from `InspectorLayout.tsx` as a pure structural move - state,
 * refs, effect and fetch body are byte-identical to the inline originals.
 * `fetchFranchiseData` is returned so the sync-complete callback can
 * refresh the record after a sync lands.
 */
import { useEffect, useRef, useState } from 'react'
import { apiClient } from '../services/api'
import { isRequestCanceled } from '../services/core/errorHandler'
import { logger } from '../utils/logger'
import type { FranchisePartnerResource } from '../types/resources'

export interface FranchiseData {
  id?: number
  fp_code: string
  franchise_name: string
  country: string
  state: string
  last_sync_date?: string
  xero_short_code?: string
  field_management_system?: string
  lightspeed_store_code?: string
  has_complete_lightspeed_connection?: boolean
  has_complete_xero_connection?: boolean
  has_complete_skimmer_connection?: boolean
  resources?: FranchisePartnerResource[]
}

export function useFranchiseData(fpCode?: string) {
  const [franchiseData, setFranchiseData] = useState<FranchiseData | null>(null)
  const [loading, setLoading] = useState(true)
  const [fpNotFound, setFpNotFound] = useState(false)

  // Request deduplication for franchise partner API calls
  const abortControllerRef = useRef<AbortController | null>(null)
  const requestInProgressRef = useRef<boolean>(false)

  const fetchFranchiseData = async () => {
    if (!fpCode) {
      setLoading(false)
      return
    }

    // Check if request is already in progress
    if (requestInProgressRef.current) {
      logger.debug('⚠️ InspectorLayout: Franchise partner request already in progress, skipping')
      return
    }

    // Cancel any previous request
    if (abortControllerRef.current) {
      logger.debug('🚫 InspectorLayout: Canceling previous franchise partner request')
      abortControllerRef.current.abort()
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController()
    requestInProgressRef.current = true

    try {
      setLoading(true)
      logger.debug('🚀 InspectorLayout: Making franchise partner API request')

      const response = await apiClient.get(`/franchise-partners/${fpCode}`, {
        signal: abortControllerRef.current.signal
      })

      // Only update state if request wasn't cancelled
      if (!abortControllerRef.current.signal.aborted) {
        setFranchiseData({
          id: response.data.id,
          fp_code: response.data.fp_code,
          franchise_name: response.data.franchise_name,
          country: response.data.country,
          state: response.data.state,
          last_sync_date: response.data.last_sync_at,
          xero_short_code: response.data.xero_short_code,
          field_management_system: response.data.field_management_system,
          lightspeed_store_code: response.data.lightspeed_store_code,
          has_complete_lightspeed_connection: response.data.has_complete_lightspeed_connection,
          has_complete_xero_connection: response.data.has_complete_xero_connection,
          has_complete_skimmer_connection: response.data.has_complete_skimmer_connection,
          resources: response.data.resources
        })
      }
    } catch (err: any) {
      // Don't show errors for cancelled requests
      if (isRequestCanceled(err)) {
        logger.debug('🚫 InspectorLayout: Franchise partner request was cancelled')
        return
      }

      const status = err.response?.status
      if (status === 404 || status === 403) {
        logger.warn(`Franchise partner unavailable (${status}): ${fpCode}`)
        setFpNotFound(true)
        return
      }

      // A 5xx / network failure is a SERVER fault, not a missing FP - log it
      // with the status so a backend error is greppable and never silently
      // hides behind the generic "not found" screen (a missing import once
      // 500'd here and read as a 404 to the user). We still fall through to
      // the not-found view to avoid rendering a dataless shell.
      logger.error(
        `Franchise fetch failed for ${fpCode} (status: ${status ?? 'network/unknown'})`,
        err,
      )
      setFpNotFound(true)
    } finally {
      requestInProgressRef.current = false
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchFranchiseData()

    // Cleanup function to cancel any in-flight requests
    return () => {
      if (abortControllerRef.current) {
        logger.debug('🧹 InspectorLayout: canceling franchise partner request on unmount')
        abortControllerRef.current.abort()
      }
    }
    // Only refetch on a real FP change - in-FP navigation reuses the cached
    // franchise data. Refetching on every pathname change flipped `loading`,
    // which churned the `!loading`-gated children (alert banner, status chips)
    // and caused the freshness banner to pop in late. Freshness-after-sync is
    // covered separately by the evop:sync-completed handler in the layout.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fpCode])

  return { franchiseData, loading, fpNotFound, fetchFranchiseData }
}
